<template>
  <div
    class="tltp"
    v-if="(isReady || isLoading) && targetEl"
    :style="{
      top: tooltipPosition.top + 'px',
      left: tooltipPosition.left + 'px',
      zIndex: 99999,
      pointerEvents: pointerEventsState
    }"
    @mouseenter="handleTooltipMouseEnter"
    @mouseleave="handleTooltipMouseLeave"
  >
    <div 
      class="tltp__wrapper" 
      :style="{
        opacity: wrapperOpacity,
        transform: wrapperTransform
      }"
      ref="wrapperEl"
    >
      <template v-if="isReady">
        <div class="tltp__body">
          <AvatarImage class="tltp__avatar" :src="data.image" :size="24" />
          <div class="tltp__hdr">
            <span class="tltp__name">{{ data.symbol }}</span>
            <span class="tltp__price">${{ data.price_usd }}</span>
            <span
              class="tltp__percent"
              :class="data.change_1d_usd < 0 ? 'tltp__percent--red' : 'tltp__percent--green'"
            >
              {{ data.change_1d_usd }}%
            </span>
            <a class="tltp__drops-icon" :href="data.dropstab_url" target="_blank">
              <DropstabIcon />
            </a>
            <button class="tltp__forecaster-icon" @click="goToForecasters()">
              <ForecastersIcon />
            </button>
          </div>
          <div class="tltp__footer">
            <p class="tltp__footer-item">
              <span class="tltp__footer-prefix">#</span> {{ data.rank }}
            </p>
            <p class="tltp__footer-item">
              <span class="tltp__footer-prefix">MC</span> {{ data.market_cap }}
            </p>
            <p class="tltp__footer-item">
              <span class="tltp__footer-prefix">FDV</span> ${{ data.fdv }}
            </p>
          </div>
        </div>
      </template>
      <template v-else-if="isLoading">
        <div class="tltp__loader">
          <StubsBlock />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
  import { useStore } from 'vuex';
  import { throttle } from 'lodash';
  import { useRouter } from 'vue-router';
  import { PATHS_NAME } from '@shared/routers/consts/pathsName';
  import { closeContentPopup } from '@/Groups/CoinTooltip/private/imports/closeModalContent';
  import { callForecasterCoinFilter } from '@/Groups/Forecasters/public/utils/activeForecasterFilter';
  //----------------------------------------
  import { AvatarImage, ForecastersIcon, DropstabIcon } from '@/Groups/CoinTooltip/private/imports/externalTemplates';
  import StubsBlock from '@/Groups/CoinTooltip/private/ui/StubsBlock/StubsBlock.vue';

  //=============================================
  // КОНФИГУРАЦИЯ АНИМАЦИИ
  //=============================================
  const ANIMATION_CONFIG = {
    //Длительность анимации в ms
    DURATION: 100,
    //Смещение по Y в px 
    TRANSLATE_OFFSET: 10,
    //Задержка перед закрытием при уходе мыши
    MOUSE_LEAVE_DELAY: 50,
    //Задержка перед полным закрытием
    CLOSE_DELAY: 150,
    //Задержка для throttle
    THROTTLE_DELAY: 250
  };

  //=============================================
  // ИНИЦИАЛИЗАЦИЯ СТОРА И РОУТЕРА
  //=============================================
  const store = useStore();
  const router = useRouter();

  //=============================================
  // РЕАКТИВНЫЕ ПЕРЕМЕННЫЕ
  //=============================================
  //Позиция тултипа на странице (top, left)
  const tooltipPosition = ref({ top: 0, left: 0 });
  
  //Целевой DOM-элемент, к которому привязан тултип
  const targetEl = ref(null);
  
  //Флаг наведения курсора на сам тултип
  const isHoveringTooltip = ref(false);
  
  //Прозрачность обертки тултипа для анимации
  const wrapperOpacity = ref(0);
  
  //Transform для анимации появления/скрытия
  const wrapperTransform = ref(`translateY(${ANIMATION_CONFIG.TRANSLATE_OFFSET}px)`);
  
  //Состояние pointer-events (auto/none) для управления интерактивностью
  const pointerEventsState = ref('none');
  
  //Высота тултипа для правильного позиционирования
  const tooltipHeight = ref(0);
  
  //Флаг монтирования компонента для предотвращения преждевременных анимаций
  const isMounted = ref(false);
  
  //ID текущей загруженной монеты для предотвращения перезагрузки
  const currentCoinId = ref(null);
  
  //ID для requestAnimationFrame
  let animationFrameId = null;
  
  //ID таймаутов для возможности их отмены
  let mouseLeaveTimeoutId = null;
  let closeTimeoutId = null;

  //=============================================
  // COMPUTED СВОЙСТВА ИЗ VUEX
  //=============================================
  
  //Флаг открытия тултипа
  const isOpen = computed(() => store.getters['CoinTooltip/getIsOpen']);
  
  //Флаг загрузки данных
  const isLoading = computed(() => store.getters['CoinTooltip/getIsLoading']);
  
  //Флаг готовности данных для отображения
  const isReady = computed(() => store.getters['CoinTooltip/getIsReady']);
  
  //Данные о монете для отображения в тултипе
  const data = computed(() => store.getters['CoinTooltip/getData']);

  //=============================================
  // УТИЛИТЫ
  //=============================================
  
  //Очищает все активные таймауты
  const clearAllTimeouts = () => {
    if (mouseLeaveTimeoutId) {
      clearTimeout(mouseLeaveTimeoutId);
      mouseLeaveTimeoutId = null;
    }
    if (closeTimeoutId) {
      clearTimeout(closeTimeoutId);
      closeTimeoutId = null;
    }
  };

  //=============================================
  // ФУНКЦИИ АНИМАЦИИ
  //=============================================

  //Парсит значение translateY из строки transform
  const parseTransformY = (transform) => {
    const match = transform.match(/translateY\(([^)]+)px\)/);
    return match ? parseFloat(match[1]) : 0;
  };

  //Основная функция анимации opacity и transform с easing
  const animateTooltip = (targetOpacity, targetTransform, duration = ANIMATION_CONFIG.DURATION) => {
    
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    const startOpacity = wrapperOpacity.value;
    const startTransform = wrapperTransform.value;
    
    const startY = parseTransformY(startTransform);
    const targetY = parseTransformY(targetTransform);
    
    const startTime = performance.now();

    pointerEventsState.value = 'none';

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeProgress = progress < 0.5 
        ? 2 * progress * progress 
        : -1 + (4 - 2 * progress) * progress;

      wrapperOpacity.value = startOpacity + (targetOpacity - startOpacity) * easeProgress;

      const currentY = startY + (targetY - startY) * easeProgress;
      wrapperTransform.value = `translateY(${currentY}px)`;

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        wrapperOpacity.value = targetOpacity;
        wrapperTransform.value = targetTransform;
        animationFrameId = null;
        
        if (targetOpacity === 1) {
          pointerEventsState.value = 'auto';
        }
      }
    };

    animationFrameId = requestAnimationFrame(animate);
  };

  //Плавное открытие тултипа
  const showTooltipWithAnimation = () => {
    clearAllTimeouts();
    animateTooltip(1, 'translateY(0px)');
  };

  //Плавное закрытие тултипа
  const hideTooltipWithAnimation = () => {
    pointerEventsState.value = 'none';
    animateTooltip(0, `translateY(${ANIMATION_CONFIG.TRANSLATE_OFFSET}px)`);
  };

  //=============================================
  // ФУНКЦИИ ПОЗИЦИОНИРОВАНИЯ
  //=============================================
  
  //Находит элемент под курсором для показа тултипа
  const tooltipElement = (e) => {
    return (
      e.target.closest('.title-tooltip-coin') ||
      e.target.closest('.Coin-Info-Tooltip') ||
      e.target.closest('[data-coin]')
    );
  };

  //Обновляет позицию тултипа относительно целевого элемента
  const updateTooltipPosition = async (el) => {
    if (!el) return;
    await nextTick();
    
    const targetRect = el.getBoundingClientRect();
    const tooltipEl = document.querySelector('.tltp__wrapper');
    const tooltipWidth = tooltipEl?.offsetWidth || 220;

    tooltipHeight.value = tooltipEl?.offsetHeight || 100;
    
    const margin = 8;
    const viewportWidth = window.innerWidth;

    let top = targetRect.top + window.scrollY - tooltipHeight.value - margin;
    let left = targetRect.left + window.scrollX + targetRect.width / 2 - tooltipWidth / 2;
    
    if (top < window.scrollY) {
      top = targetRect.bottom + window.scrollY + margin;
    }
    
    if (left < 0) left = margin;
    if (left + tooltipWidth > viewportWidth) left = viewportWidth - tooltipWidth - margin;
    
    tooltipPosition.value = { top, left };
  };

  //=============================================
  // ФУНКЦИИ УПРАВЛЕНИЯ ТУЛТИПОМ
  //=============================================
  //Вызывает показ тултипа для элемента
  const callTooltip = async (e) => {
    const el = tooltipElement(e);
    if (!el) return;
    
    const dataCoin = el.getAttribute('data-coin');
    if (!dataCoin) return;
    
    //Проверяем, не та же ли это монета
    if (currentCoinId.value === dataCoin) {
      //Обновляем только позицию, если элемент изменился
      if (targetEl.value !== el) {
        targetEl.value = el;
        await updateTooltipPosition(el);
      }
      return;
    }
    
    //Новая монета - загружаем данные
    currentCoinId.value = dataCoin;
    targetEl.value = el;
    
    await updateTooltipPosition(el);
    store.dispatch('CoinTooltip/toggleOpenedFlag', true);
    store.dispatch('CoinTooltip/init', dataCoin);
  };

  //Полностью закрывает тултип
  const closeTooltip = () => {
    //Не закрываем если курсор на тултипе
    if (isHoveringTooltip.value) return;
    
    clearAllTimeouts();
    currentCoinId.value = null;
    store.dispatch('CoinTooltip/closeTooltip');
    targetEl.value = null;
  };

  //Универсальная функция закрытия с задержкой
  const scheduleClose = () => {
    clearAllTimeouts();
    hideTooltipWithAnimation();
    
    closeTimeoutId = setTimeout(() => {
      if (!isHoveringTooltip.value) {
        closeTooltip();
      }
      closeTimeoutId = null;
    }, ANIMATION_CONFIG.CLOSE_DELAY);
  };

  //=============================================
  // ОБРАБОТЧИКИ СОБЫТИЙ МЫШИ
  //=============================================
  //Обработчик наведения на тултип
  const handleTooltipMouseEnter = () => {
    isHoveringTooltip.value = true;
    clearAllTimeouts();
  };

  //Обработчик ухода с тултипа
  const handleTooltipMouseLeave = () => {
    isHoveringTooltip.value = false;
    
    mouseLeaveTimeoutId = setTimeout(() => {
      if (!isHoveringTooltip.value) {
        scheduleClose();
      }
      mouseLeaveTimeoutId = null;
    }, ANIMATION_CONFIG.MOUSE_LEAVE_DELAY);
  };

  //Проверяет положение курсора и управляет тултипом
  const checkCursorOnTicker = (e) => {
    const el = tooltipElement(e);
    
    if (!el) {
      //Проверяем, не наведен ли курсор на сам тултип
      const isOnTooltip = e.target.closest('.tltp');
      if (!isOnTooltip) {
        scheduleClose();
      }
      return;
    }
    
    callTooltip(e);
  };

  //Обработчик клика по элементу с тултипом
  const clickCoin = (e) => {
    if (!tooltipElement(e)) return;
    e.preventDefault();
    callTooltip(e);
  };

  //Обработчик mouseout документа (курсор покидает элемент)
  const handleDocumentMouseOut = (e) => {
    if (!e.relatedTarget) {
      scheduleClose();
      return;
    }
    
    const fromEl = e.target.closest('[data-coin], .title-tooltip-coin, .Coin-Info-Tooltip');
    if (!fromEl) return;

    const toEl = e.relatedTarget.closest('[data-coin], .title-tooltip-coin, .Coin-Info-Tooltip, .tltp');
    if (toEl) return;

    scheduleClose();
  };

  //Обработчик mouseleave документа (курсор покинул окно браузера)
  const handleDocumentMouseLeave = (e) => {
    if (!e.relatedTarget) {
      scheduleClose();
    }
  };

  //=============================================
  // ФУНКЦИИ НАВИГАЦИИ
  //=============================================
  
  //Переход к странице форкастеров с фильтром по монете
  const goToForecasters = () => {
    const coin = {
      api_id: `${data.value.id}`,
      image: data.value.image,
      name: data.value.name,
      symbol: data.value.symbol,
      dropstab_url: data.value.dropstab_url,
    };
    
    router.push({ name: PATHS_NAME.FORECASTERS });
    closeContentPopup();
    callForecasterCoinFilter(coin);
  };

  //=============================================
  // THROTTLE ФУНКЦИИ
  //=============================================
  
  //Троттлированная функция проверки курсора
  const throttledMouseMove = throttle(checkCursorOnTicker, ANIMATION_CONFIG.THROTTLE_DELAY);

  //=============================================
  // LIFECYCLE ХУКИ
  //=============================================
  
  onMounted(() => {
    isMounted.value = true;
    document.addEventListener('mousemove', throttledMouseMove);
    document.addEventListener('click', clickCoin);
    document.addEventListener('mouseout', handleDocumentMouseOut);
    document.addEventListener('mouseleave', handleDocumentMouseLeave);
  });

  onUnmounted(() => {
    isMounted.value = false;
    
    document.removeEventListener('mousemove', throttledMouseMove);
    document.removeEventListener('click', clickCoin);
    document.removeEventListener('mouseout', handleDocumentMouseOut);
    document.removeEventListener('mouseleave', handleDocumentMouseLeave);
    if (throttledMouseMove && throttledMouseMove.cancel) {
      throttledMouseMove.cancel();
    }
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    clearAllTimeouts();
  });

  //=============================================
  // WATCHERS
  //=============================================
  //Обновляем позицию при изменении состояния загрузки или готовности
  watch([isReady, isLoading], async () => {
    if ((isReady.value || isLoading.value) && targetEl.value) {
      await nextTick();
      updateTooltipPosition(targetEl.value);
    }
  });

  //Управляем анимацией при открытии/закрытии тултипа
  watch(isOpen, (newVal) => {
    if (!isMounted.value) return;
    if (newVal) {
      showTooltipWithAnimation();
    } else {
      if (!isHoveringTooltip.value) {
        hideTooltipWithAnimation();
      }
    }
  });
</script>

<style lang="scss" scoped>
  @import "./styles.scss";
</style>